import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../../api';
import AddressFields from '../../components/common/address/AddressFields';
import { DatePicker } from '../../components/common/DatePicker';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import PhoneNumberFields from '../../components/common/PhoneNumberFields';
import { validateStoredPhone } from '../../utils/phone';
import { getApiErrorInfo, apiErrorMessage } from '../../utils/apiError';
import { showConfirm } from '../../utils/popup';
import { TextInput, SelectInput, CheckboxField, FormField } from '../../components/common/form';
import { parseRegionalDate, toISODateFromRegional, academyTodayDate } from '../../utils/date';
// --- end helpers ---


import {
    addressPayloadFields,
    hasAnyAddress as hasAnyAddressUtil,
    validateAddressIfPresent,
} from '../../components/common/address/addressValidators';
import { formatDate } from '../../utils/date';
import ArchiveStudentModal from '../../components/students/ArchiveStudentModal';
import { checkStudentArchive, archiveStudentGuided, archiveUser } from '../../api/users';
import { isValidPersonName } from '../../utils/name';
import { studentDisplayName as formatStudentFallback } from '../../utils/displayNames';


const ROLE_CONST = 'STUDENT';
const ALL_ROLES = ['ADMIN', 'MANAGER', 'COACH', 'STUDENT'];

const StudentForm = ({ managerMode = false }) => {
    const { t } = useTranslation();
    const apiPrefix = managerMode ? '/manager' : '/admin';
    const listPath = managerMode ? '/manager/students' : '/admin/students';
    const selectableRoles = managerMode ? [ROLE_CONST] : ALL_ROLES;

    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [loadError, setLoadError] = useState('');
    const [sendInvite, setSendInvite] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archiveCheck, setArchiveCheck] = useState(null);
    const [archiveCheckLoading, setArchiveCheckLoading] = useState(false);
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);
    const [saving, setSaving] = useState(false);


    const navigate = useNavigate();

    // Unified form state
    const [form, setForm] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        gender: 'MALE',
        username: '',
        email: '',
        phoneNumber: '',
        active: true,
        sendInvite: true, // create only
        fideId: '',
        eloRating: '',
        fideTitle: '',

        // roles state as array (backend requires at least one role)
        roles: [ROLE_CONST],

        // Address
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        latitude: '',
        longitude: '',
    });

    // Robust role extraction for edit mode (backend may return role, roles, userRoles, etc.)
    const extractRoles = (u) => {
        if (!u) return [ROLE_CONST];

        const toName = (r) => {
            if (typeof r === 'string') return r;
            if (!r) return ROLE_CONST;
            return r.name || r.role || r.code || ROLE_CONST;
        };

        if (Array.isArray(u.roles) && u.roles.length) return u.roles.map(toName);
        if (Array.isArray(u.userRoles) && u.userRoles.length) return u.userRoles.map(toName);
        if (u.role) return [toName(u.role)];

        return [ROLE_CONST];
    };

    // Load on edit
    useEffect(() => {
        if (!isEditMode) return;
        setInitialLoading(true);
        API.get(`${apiPrefix}/users/${id}`)
            .then((res) => {
                const u = res.data || {};
                const a = u.address || {};
                setForm({
                    firstName: u.firstName || u.first_name || '',
                    middleName: u.middleName || u.middle_name || '',
                    lastName: u.lastName || u.last_name || '',
                    dob: u.dob || '',
                    gender: u.gender || 'MALE',
                    username: u.username || '',
                    email: u.email || '',
                    phoneNumber: u.phoneNumber || u.phone_number || '',
                    active: u.active !== false,
                    sendInvite: false, // not used in edit
                    fideId: u.fideId || '',
                    eloRating: u.eloRating != null ? String(u.eloRating) : '',
                    fideTitle: u.fideTitle || '',
                    roles: extractRoles(u),

                    addressId: u.address?.id ?? null,
                    addressLine1: a.addressLine1 || '',
                    addressLine2: a.addressLine2 || '',
                    city: a.city || '',
                    state: a.state || '',
                    country: a.country || '',
                    pincode: a.pincode || '',
                    latitude: a.latitude != null ? String(a.latitude) : '',
                    longitude: a.longitude != null ? String(a.longitude) : '',
                });
            })
            .catch((err) => {
                console.error('Error fetching student:', err);
                setLoadError(t('common.listLoadFailed'));
            })
            .finally(() => setInitialLoading(false));
    }, [id, isEditMode, apiPrefix, t]);

    // Helpers
    const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    const studentDisplayName = [form.firstName, form.lastName].filter(Boolean).join(' ').trim()
        || form.username
        || formatStudentFallback(null, id, t);

    const openArchiveModalFromForm = async () => {
        setArchiveModalOpen(true);
        setArchiveCheckLoading(true);
        setArchiveCheck(null);
        try {
            const res = await checkStudentArchive(id);
            setArchiveCheck(res?.data ?? res);
        } catch (err) {
            console.error(err);
            setArchiveModalOpen(false);
            alert(apiErrorMessage(err, t('forms.student.loadArchiveFailed')));
        } finally {
            setArchiveCheckLoading(false);
        }
    };

    const handleActiveToggle = async (nextActive) => {
        if (!nextActive && isEditMode && !managerMode && form.active) {
            await openArchiveModalFromForm();
            return;
        }
        setField('active', nextActive);
    };

    const handleStudentArchiveConfirm = async (payload) => {
        setArchiveSubmitting(true);
        try {
            if (archiveCheck?.hasActiveEnrollments) {
                await archiveStudentGuided(id, payload);
            } else {
                await archiveUser(id);
            }
            setArchiveModalOpen(false);
            if (payload.mode === 'END_ENROLLMENTS_ONLY') {
                setField('active', true);
                alert(t('forms.student.enrollmentsEnded'));
            } else {
                setField('active', false);
                alert(t('forms.student.archived'));
                navigate(listPath, { state: { fromForm: Date.now() } });
            }
        } catch (err) {
            console.error(err);
            alert(apiErrorMessage(err, t('forms.student.archiveFailed')));
        } finally {
            setArchiveSubmitting(false);
        }
    };

    // Roles UI (chips), same pattern as Manager/Coach
    const RoleChip = ({ role }) => {
        const active = (form.roles || []).includes(role);
        return (
            <button
                type="button"
                onClick={() => {
                    setForm((f) => {
                        const has = (f.roles || []).includes(role);
                        const next = has
                            ? (f.roles || []).filter((x) => x !== role)
                            : [...(f.roles || []), role];
                        return { ...f, roles: next };
                    });
                }}
                className={active ? 'role-chip role-chip-active' : 'role-chip'}
            >
                {t(`faq.roles.${role}`, { defaultValue: role })}
            </button>
        );
    };

    // Validations
    const validate = () => {
        if (!form.firstName || !String(form.firstName).trim()) {
            alert(t('forms.validation.firstNameRequired'));
            return false;
        }
        if (!isValidPersonName(form.firstName)) {
            alert(t('forms.validation.firstNameLetters'));
            return false;
        }
        if (form.middleName && !isValidPersonName(form.middleName)) {
            alert(t('forms.validation.middleNameLetters'));
            return false;
        }
        if (!form.lastName || !String(form.lastName).trim()) {
            alert(t('forms.validation.lastNameRequired'));
            return false;
        }
        if (!isValidPersonName(form.lastName)) {
            alert(t('forms.validation.lastNameLetters'));
            return false;
        }

        if (form.dob) {
            const d = parseRegionalDate(form.dob);
            const today = academyTodayDate();
            if (d >= today) {
                alert(t('forms.validation.invalidDob'));
                return false;
            }
        }

        if (!isEditMode) {
            if (!form.username || form.username.trim().length === 0) {
                alert(t('forms.validation.usernameRequired'));
                return false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!form.email || !emailRegex.test(form.email)) {
                alert(t('forms.validation.emailInvalid'));
                return false;
            }
        }

        const phoneCheck = validateStoredPhone(form.phoneNumber);
        if (!phoneCheck.valid) {
            alert(phoneCheck.message);
            return false;
        }

        if (!isEditMode && !form.sendInvite) {
            if (!password || password.length < 6) {
                alert(t('forms.validation.passwordMin'));
                return false;
            }
            if (password !== confirmPassword) {
                alert(t('forms.validation.passwordsMismatch'));
                return false;
            }
        }


        // Must have at least one role
        if (!form.roles || !Array.isArray(form.roles) || form.roles.length === 0) {
            alert(t('forms.validation.rolesRequired'));
            return false;
        }

        // Address: optional; validate only if present using shared validators
        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };

        const addressPresent = hasAnyAddressUtil(addressObj);
        if (addressPresent) {
            const v = validateAddressIfPresent(addressObj);
            if (v && v.ok === false) {
                alert(v.message || t('forms.validation.invalidAddress'));
                return false;
            }
            if (form.latitude && isNaN(parseFloat(form.latitude))) {
                alert(t('forms.validation.invalidLatitude'));
                return false;
            }
            if (form.longitude && isNaN(parseFloat(form.longitude))) {
                alert(t('forms.validation.invalidLongitude'));
                return false;
            }
        }

        return true;
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving || !validate()) return;

        // Always send roles as an array — backend requires it
        const rolesPayload =
            Array.isArray(form.roles) && form.roles.length ? form.roles : [ROLE_CONST];

        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };

        const payload = {
            firstName: form.firstName,
            middleName: form.middleName,
            lastName: form.lastName,
            dob: toISODateFromRegional(form.dob) || null,
            gender: form.gender,
            username: form.username,
            email: form.email,
            phoneNumber: validateStoredPhone(form.phoneNumber).normalized,
            active: form.active,
            roles: rolesPayload, // multi-role support
            fideId: form.fideId?.trim() || null,
            eloRating: form.eloRating !== '' && form.eloRating != null ? Number(form.eloRating) : null,
            fideTitle: form.fideTitle?.trim() || null,

            ...addressPayloadFields(addressObj, { addressId: form.addressId }),

            ...(isEditMode
                ? {}
                : form.sendInvite
                    ? { sendInvite: true }
                    : { sendInvite: false, password }
            ),

        };

        setSaving(true);
        try {
            if (isEditMode) {
                await API.put(`${apiPrefix}/users/${id}`, payload);
            } else {
                await API.post(`${apiPrefix}/users`, payload);
            }
            alert(
                isEditMode
                    ? t('forms.student.updated')
                    : form.sendInvite
                        ? t('accountAccess.signInLink.success')
                        : t('forms.student.created')
            );
            navigate(listPath, { state: { fromForm: Date.now() } });
        } catch (err) {
            console.error('Error saving student:', err);
            const errorInfo = getApiErrorInfo(
                err,
                isEditMode ? t('forms.student.updateFailed') : t('forms.student.saveFailed'),
            );
            alert(`${errorInfo.title}\n\n${errorInfo.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (initialLoading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEditMode ? t('forms.student.editTitle') : t('forms.student.addTitle')}
                subtitle={t('forms.student.subtitle')}
            />
        );
    }

    if (isEditMode && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.student.editTitle')}
                    subtitle={t('forms.student.subtitle')}
                />
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {loadError}
                </div>
                <button type="button" className="btn-page-action-secondary mt-4" onClick={() => navigate(listPath)}>
                    {t('common.back')}
                </button>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={isEditMode ? t('forms.student.editTitle') : t('forms.student.addTitle')}
                subtitle={t('forms.student.subtitle')}
            />

            <form onSubmit={handleSubmit} className="form-shell">
                {/* Names */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-3">
                    <TextInput
                        label={t('forms.common.firstName')}
                        name="firstName"
                        value={form.firstName}
                        onChange={(e) => setField('firstName', e.target.value)}
                        placeholder={t('forms.placeholders.firstNameRequired')}
                        required
                    />
                    <TextInput
                        label={t('forms.common.middleName')}
                        name="middleName"
                        value={form.middleName}
                        onChange={(e) => setField('middleName', e.target.value)}
                        placeholder={t('forms.placeholders.middleName')}
                    />
                    <TextInput
                        label={t('forms.common.lastName')}
                        name="lastName"
                        value={form.lastName}
                        onChange={(e) => setField('lastName', e.target.value)}
                        placeholder={t('forms.placeholders.lastNameRequired')}
                        required
                    />
                </div>

                {/* DOB & Gender */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField label={t('forms.common.dateOfBirth')}>
                        <DatePicker value={formatDate(form.dob)} onChange={(e) => setField('dob', e)}
                            className="w-full"
                        />
                    </FormField>
                    <SelectInput
                        label={t('forms.common.gender')}
                        name="gender"
                        value={form.gender}
                        onChange={(e) => setField('gender', e.target.value)}
                        required
                    >
                        <option value="MALE">{t('forms.common.male')}</option>
                        <option value="FEMALE">{t('forms.common.female')}</option>
                        <option value="OTHER">{t('forms.common.other')}</option>
                    </SelectInput>
                </div>

                {/* FIDE / rating (European clubs) */}
                <div className="form-section">
                    <h3 className="mb-2 text-sm font-semibold text-gray-800">{t('forms.student.fideSection')}</h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <TextInput
                            label={t('forms.student.fideId')}
                            name="fideId"
                            value={form.fideId}
                            onChange={(e) => setField('fideId', e.target.value)}
                            placeholder={t('forms.student.fideIdPlaceholder', { defaultValue: 'e.g. 1503014' })}
                        />
                        <TextInput
                            label={t('forms.student.eloRating')}
                            name="eloRating"
                            type="number"
                            value={form.eloRating}
                            onChange={(e) => setField('eloRating', e.target.value)}
                            placeholder={t('forms.student.eloPlaceholder', { defaultValue: 'e.g. 1650' })}
                        />
                        <TextInput
                            label={t('forms.student.fideTitle')}
                            name="fideTitle"
                            value={form.fideTitle}
                            onChange={(e) => setField('fideTitle', e.target.value)}
                            placeholder={t('forms.student.fideTitlePlaceholder', { defaultValue: 'e.g. CM, FM, IM, GM' })}
                        />
                    </div>
                </div>

                {/* Username (create editable, edit disabled) */}
                <div className="form-section">
                    <TextInput
                        label={t('forms.common.loginUsername')}
                        name="username"
                        value={form.username}
                        onChange={(e) => setField('username', e.target.value)}
                        muted={isEditMode}
                        required
                        disabled={isEditMode}
                    />
                    <TextInput
                        label={t('forms.common.emailAddress')}
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        muted={isEditMode}
                        required={!isEditMode}
                        disabled={isEditMode}
                    />
                </div>

                {/* Phone & Active */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField label={t('forms.common.phoneNumber')} required>
                        <PhoneNumberFields
                            value={form.phoneNumber}
                            onChange={(v) => setField('phoneNumber', v)}
                            required
                        />
                    </FormField>
                    <CheckboxField
                        layout="toggle-card"
                        className="mt-6 md:mt-0"
                        label={t('forms.common.accountActive')}
                        description={
                            isEditMode && !managerMode
                                ? t('forms.student.activeDescArchive')
                                : t('forms.student.activeDesc')
                        }
                        name="active"
                        checked={form.active}
                        onChange={(e) => handleActiveToggle(e.target.checked)}
                    />
                </div>

                {/* Roles (multi-select for admin; fixed STUDENT for manager) */}
                <div className="form-section">
                    <div>
                        <div className="form-section-title">{t('forms.common.roles')}</div>
                        {managerMode ? (
                            <p className="field-note">{t('forms.student.managerRolesNote')}</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {selectableRoles.map((r) => (
                                    <RoleChip key={r} role={r} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Invite (create only) */}
                {!isEditMode && (
                    <div className="form-section">
                        <CheckboxField
                            label={t('accountAccess.signInLink.createCheckbox')}
                            checked={form.sendInvite}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setSendInvite(checked);
                                setField('sendInvite', checked);

                                if (checked) {
                                    setPassword('');
                                    setConfirmPassword('');
                                }
                            }}
                        />
                        {form.sendInvite && (
                            <p className="field-note">{t('accountAccess.signInLink.createNote')}</p>
                        )}
                    </div>
                )}

                {!isEditMode && !form.sendInvite && (
                    <div className="form-section">
                        <TextInput
                            label={t('forms.common.loginPassword')}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <TextInput
                            label={t('forms.common.confirmLoginPassword')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>
                )}


                {/* Address (optional dropdowns via AddressFields) */}
                <AddressFields
                    value={form}
                    applyDefaultCountry={!isEditMode}
                    onChange={(patchOrName, maybeValue) => {
                        if (typeof patchOrName === 'string') {
                            setField(patchOrName, maybeValue);
                        } else if (patchOrName && typeof patchOrName === 'object') {
                            setForm((prev) => ({ ...prev, ...patchOrName }));
                        }
                    }}
                />

                {/* Actions */}
                <div className="page-actions justify-end pt-2">
                    <Button type="submit" variant="gradient-primary" disabled={saving}>
                        {saving
                            ? t('forms.common.saving', { defaultValue: 'Saving…' })
                            : isEditMode
                                ? t('forms.student.updateButton')
                                : t('forms.student.createButton')}
                    </Button>

                    {isEditMode && (
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                                if (!await showConfirm(t('accountAccess.signInLink.editConfirm', { role: t('userActions.labels.student') }))) return;
                                try {
                                    await API.post(`${apiPrefix}/users/${id}/invite`);
                                    alert(t('accountAccess.signInLink.success'));
                                } catch (e) {
                                    console.error(e);
                                    alert(t('accountAccess.signInLink.failure'));
                                }
                            }}
                        >
                            {t('accountAccess.signInLink.button')}
                        </Button>
                    )}
                </div>
            </form>

            {!managerMode && isEditMode && (
                <ArchiveStudentModal
                    open={archiveModalOpen}
                    userId={id}
                    studentName={studentDisplayName}
                    archiveCheck={archiveCheck}
                    loadingCheck={archiveCheckLoading}
                    submitting={archiveSubmitting}
                    onConfirm={handleStudentArchiveConfirm}
                    onCancel={() => {
                        if (!archiveSubmitting) setArchiveModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default StudentForm;